import { cn } from '@/lib/utils';
import { Icon } from '@/components/Icon';
import type { TutorialStep } from './tutorialsModel';

interface StepListProps {
  steps: TutorialStep[];
  index: number;
  seen: readonly string[];
  onGoTo: (index: number) => void;
}

/** Every step of the lesson, in order: jump to any of them; seen ones carry a check. 44 px rows. */
export function StepList({ steps, index, seen, onGoTo }: StepListProps) {
  return (
    <nav aria-label="Lesson steps" className="flex min-w-0 flex-col rounded-lg border bg-card shadow-card">
      <div className="flex items-center justify-between gap-2 border-b px-4 py-3">
        <span className="text-sm font-semibold text-foreground">Steps</span>
        <span className="tabular text-xs text-subtle">{steps.length}</span>
      </div>
      <ol className="flex flex-col p-1.5">
        {steps.map((s, i) => {
          const active = i === index;
          const done = seen.includes(s.key) && !active;
          return (
            <li key={s.key}>
              <button
                type="button"
                data-testid={`lesson-step-${i}`}
                aria-current={active ? 'step' : undefined}
                onClick={() => onGoTo(i)}
                className={cn(
                  'lift-row flex min-h-11 w-full items-center gap-3 rounded-md px-2.5 py-2 text-left outline-none focus-visible:ring-[3px] focus-visible:ring-ring/50',
                  active ? 'bg-secondary' : 'hover:bg-accent',
                )}
              >
                <span
                  className={cn(
                    'tabular flex size-6 shrink-0 items-center justify-center rounded-full text-[11px] font-semibold',
                    active ? 'bg-foreground text-background' : 'bg-secondary text-muted-foreground',
                  )}
                >
                  {done ? <Icon name="check" size={14} /> : i + 1}
                </span>
                <span className={cn('min-w-0 flex-1 text-[13px] leading-snug', active ? 'font-semibold text-foreground' : 'text-muted-foreground')}>{s.title}</span>
              </button>
            </li>
          );
        })}
      </ol>
    </nav>
  );
}
